<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-gray-900">Dettes Clients</h1>
        <p class="text-sm text-gray-500 mt-1">Suivi des montants dus par les restaurants et livreurs pour les produits Rapidoo</p>
      </div>
    </div>

    <!-- Filters -->
    <div class="flex flex-wrap gap-3 items-center">
      <select
        v-model="filters.status"
        class="h-10 rounded-lg border border-gray-300 px-3 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
        @change="applyFilters"
      >
        <option :value="undefined">Tous les statuts</option>
        <option value="active">Actif</option>
        <option value="partially_paid">Partiellement payé</option>
        <option value="paid">Soldé</option>
      </select>

      <select
        v-model="filters.userRole"
        class="h-10 rounded-lg border border-gray-300 px-3 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
        @change="applyFilters"
      >
        <option :value="undefined">Tous les profils</option>
        <option value="restaurant_owner">Restaurants</option>
        <option value="rider">Livreurs</option>
      </select>
    </div>

    <!-- Table -->
    <DataFetcher
      dataKey="adminClientDebts"
      :filters="dataFilters"
      v-slot="{ data, loading, reload }"
    >
      <div class="rounded-2xl border border-gray-200 bg-white overflow-hidden">
        <div v-if="loading" class="shimmerContainer p-6 space-y-3">
          <div v-for="i in 5" :key="i" class="shimmerTarget h-12 rounded-lg" />
        </div>

        <div v-else-if="!data?.result?.length" class="py-16 text-center text-gray-400">
          <svg class="mx-auto mb-3 w-10 h-10 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p class="font-medium">Aucune dette trouvée</p>
          <p class="text-sm mt-1">Tous les clients sont à jour.</p>
        </div>

        <div v-else>
          <table class="w-full text-sm">
            <thead class="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
              <tr>
                <th class="px-4 py-3 text-left">Utilisateur</th>
                <th class="px-4 py-3 text-left">Produit</th>
                <th class="px-4 py-3 text-right">Montant initial</th>
                <th class="px-4 py-3 text-right">Restant dû</th>
                <th class="px-4 py-3 text-right">Payé</th>
                <th class="px-4 py-3 text-center">Statut</th>
                <th class="px-4 py-3 text-left">Date</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr
                v-for="debt in data.result"
                :key="debt.id"
                class="hover:bg-gray-50 transition-colors"
                :class="{ 'cursor-pointer': selectedDebt?.id !== debt.id }"
                @click="toggleDetails(debt)"
              >
                <td class="px-4 py-3">
                  <div class="font-medium text-gray-800 text-xs font-mono">{{ debt.userId }}</div>
                  <div class="text-xs text-gray-400 mt-0.5">
                    <span v-if="debt.userRole === 'restaurant_owner'" class="text-orange-600 font-semibold">Restaurant</span>
                    <span v-else class="text-blue-600 font-semibold">Livreur</span>
                  </div>
                </td>
                <td class="px-4 py-3 text-gray-700">{{ debt.productName || '—' }}</td>
                <td class="px-4 py-3 text-right font-medium text-gray-800">{{ formatAmount(debt.originalAmount) }}</td>
                <td class="px-4 py-3 text-right font-bold" :class="debt.remainingAmount > 0 ? 'text-rose-600' : 'text-gray-400'">
                  {{ formatAmount(debt.remainingAmount) }}
                </td>
                <td class="px-4 py-3 text-right text-green-700 font-medium">{{ formatAmount(debt.paidAmount) }}</td>
                <td class="px-4 py-3 text-center">
                  <span
                    class="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase"
                    :class="statusClass(debt.status)"
                  >
                    {{ statusLabel(debt.status) }}
                  </span>
                </td>
                <td class="px-4 py-3 text-gray-500 text-xs">{{ formatDate(debt.createdAt) }}</td>
              </tr>
            </tbody>
          </table>

          <!-- Pagination -->
          <div class="px-4 py-3 border-t border-gray-100 flex items-center justify-between text-sm text-gray-500">
            <p>{{ data.total }} dette(s) au total</p>
            <div class="flex gap-2">
              <button
                :disabled="dataFilters.page === 0"
                @click="dataFilters.page--"
                class="px-3 py-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 text-xs"
              >
                Précédent
              </button>
              <span class="px-3 py-1 text-xs">Page {{ dataFilters.page + 1 }}</span>
              <button
                :disabled="(dataFilters.page + 1) * dataFilters.limit >= data.total"
                @click="dataFilters.page++"
                class="px-3 py-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 text-xs"
              >
                Suivant
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Debt history drawer (expandable) -->
      <div v-if="selectedDebt" class="rounded-2xl border border-gray-200 bg-white p-6 space-y-3">
        <div class="flex items-center justify-between">
          <h3 class="font-semibold text-gray-800 text-sm">Historique des paiements — {{ selectedDebt.productName }}</h3>
          <button @click="selectedDebt = null" class="text-gray-400 hover:text-gray-600 text-xs">Fermer</button>
        </div>
        <div v-if="!selectedDebt.history?.length" class="text-gray-400 text-sm">Aucun historique de défalcation disponible.</div>
        <div
          v-for="(entry, i) in selectedDebt.history"
          :key="i"
          class="flex items-center justify-between text-sm py-2 border-b border-gray-50 last:border-0"
        >
          <div>
            <span class="text-gray-700">{{ formatDate(entry.date) }}</span>
            <span class="ml-2 text-xs text-gray-400">{{ entry.reason }}</span>
            <span v-if="entry.deficit" class="ml-2 text-xs text-rose-500">(déficit: {{ formatAmount(entry.deficit) }})</span>
          </div>
          <span class="font-semibold" :class="entry.amountDeducted > 0 ? 'text-green-700' : 'text-gray-400'">
            {{ entry.amountDeducted > 0 ? `+ ${formatAmount(entry.amountDeducted)}` : formatAmount(0) }}
          </span>
        </div>
      </div>
    </DataFetcher>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import DataFetcher from '@/features/data/presentation/components/DataFetcher.vue'
import Commons from '@/shared/utils/commons'
import moment from 'moment'

const selectedDebt = ref(null)

const filters = reactive({
  status: undefined,
  userRole: undefined,
})

const dataFilters = reactive({
  page: 0,
  limit: 20,
  status: undefined,
  userRole: undefined,
})

const applyFilters = () => {
  dataFilters.page = 0
  dataFilters.status = filters.status
  dataFilters.userRole = filters.userRole
}

const toggleDetails = (debt) => {
  selectedDebt.value = selectedDebt.value?.id === debt.id ? null : debt
}

const formatAmount = (v) => Commons.formatNumber(v || 0, { suffix: ' F' })
const formatDate = (v) => v ? moment(v).format('DD/MM/YYYY HH:mm') : '—'

const statusLabel = (s) => {
  if (s === 'active') return 'Active'
  if (s === 'partially_paid') return 'Partielle'
  if (s === 'paid') return 'Soldée'
  return s || '—'
}

const statusClass = (s) => {
  if (s === 'active') return 'bg-rose-50 text-rose-600 border border-rose-200'
  if (s === 'partially_paid') return 'bg-orange-50 text-orange-600 border border-orange-200'
  if (s === 'paid') return 'bg-green-50 text-green-600 border border-green-200'
  return 'bg-gray-100 text-gray-500'
}
</script>
